import { createAuthFlow, type SignInMethod } from "@aybuben/client/auth/flow.ts";
import type { AuthSource, AuthUser } from "@aybuben/client/auth/source.ts";
import { useEffect, useState } from "react";
import { useStore } from "zustand";

import { LinkBtn } from "../auth/fields.tsx";
import { FlowBody } from "../auth/FlowBody.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { Sheet } from "./primitives.tsx";

interface SaveProgressSheetProps {
	auth: AuthSource;
	/** An address attached but not yet confirmed, to start from. */
	email?: string;
	/** False past the guest wall: the only way on is to save. */
	dismissible: boolean;
	onDismiss: () => void;
	onLinked: (u: AuthUser, method: SignInMethod) => void;
	onLeaveGuest: (email: string | null) => Promise<void>;
	/** True while the code step is up: the guest session may be swapped away there. */
	onSaving?: (saving: boolean) => void;
}

export function SaveProgressSheet({
	auth,
	email,
	dismissible,
	onDismiss,
	onLinked,
	onLeaveGuest,
	onSaving,
}: SaveProgressSheetProps) {
	const t = useStrings();
	const [flow] = useState(() => createAuthFlow({ auth, guest: true }));
	const step = useStore(flow, (s) => s.step);
	const onCode = step.k === "code";
	useEffect(() => {
		onSaving?.(onCode);
		return () => onSaving?.(false);
	}, [onCode]);
	useEffect(() => {
		if (step.k === "done") onLinked(step.user, step.method);
	}, [step]);
	return (
		<Sheet onClose={dismissible ? onDismiss : () => {}}>
			<FlowBody
				flow={flow}
				guest
				initialEmail={email}
				onLeaveGuest={onLeaveGuest}
				entryHeader={
					<>
						<h2 className="text-xl font-extrabold">{t("save.title")}</h2>
						<p className="mt-2 text-sm leading-relaxed" style={{ color: COL.mute }}>
							{t(dismissible ? "save.body" : "save.hardBody")}
						</p>
					</>
				}
			/>
			{dismissible && step.k === "entry" && (
				<LinkBtn onClick={onDismiss}>{t("save.notNow")}</LinkBtn>
			)}
		</Sheet>
	);
}
